<template>
  <el-col :span="24" id="freefields">
    <el-row type="flex" justify="start" align="middle" id="freefields-header">
      <HeaderInfoText :colspan="9" title="FREE FIELDS" :meta="meta" :selection="selection" />
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            :current-page.sync="currentPage">
          </el-pagination>
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <div v-if="deleting" class="delete-notif">Deleting...</div>
        <el-select
          :disabled="!(selection && selection.length)"
          v-model="batchUpdateSelection"
          placeholder="With selected..."
          style="padding: 0 6px;"
          @change="handleBatchUpdateSelection">
          <el-option
            v-for="option in batchUpdateOptions"
            :key="option.id"
            :label="option.text"
            :value="option.id"
            :disabled="option.disabled">
          </el-option>
        </el-select>
        <el-button :disabled="!hasCreatePermission" type="primary" icon="plus" @click="createNewModel" />
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <el-table
          v-if="meta.count && !changingPage"
          id="freefields-table"
          :ref="`${endpoint}Table`"
          :data="pageData"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight"
          @select="handleTableSelectionChange"
          @select-all="handleTableSelectionChange"
          :row-class-name="tableRowClassName">
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="code" label="Code" align="center" width="120">
            <template slot-scope="scope">
              <el-row type="flex" justify="center">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.code }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="Name" align="left" fit>
            <template slot-scope="scope">
              <el-row type="flex">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.name }}
                </a>
              </el-row>
            </template>
          </el-table-column>
        </el-table>
        <el-row
          v-if="!receiving && !meta.count"
          align="middle"
          justify="center"
          class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
    <el-dialog
      :title="editMode.active ? hasCreatePermission ? `Edit Free Field` : `View Free Field` : 'Create New Free Field'"
      size="small"
      :visible.sync="dialogFormVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false">
      <NewEntityForm
        :modalOpen="dialogFormVisible"
        :fields="fields"
        :form.sync="form"
        :idPrefix="'a-'"
        ref="form"
        @formChanged="trackFormChange"
      />
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="saving || !!updating" @click="closeFormModal">Cancel</el-button>
        <el-button :disabled="!hasCreatePermission" :loading="saving || !!updating" type="primary" @click="submitForm">Submit</el-button>
      </span>
    </el-dialog>
  </el-col>
</template>

<script>
  import { payload } from '@/components/RouteComponents/Freefields/actionPayload'
  import store from '@/store/store'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { createNewModel } from '@/components/RouteComponents/commonFunctions'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

  export default {
    components: {
      NewEntityForm,
      HeaderInfoText
    },
    mixins: [ systemViewMixins ],
    data () {
      return {
        loadText: '',
        tableHeight: 1000,
        busy: false,
        loadingComponent: null,
        selection: [],
        batchUpdateOptions: [
          { id: 0, text: 'Delete selected', type: 'delete', disabled: false }
        ],
        batchUpdateSelection: null,
        dialogFormVisible: false,
        changingPage: false,
        editMode: { active: false, modelId: null }
      }
    },
    methods: {
      /**
       * Resets editMode data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        createNewModel(this)
      },
      updateBatchUpdateOptions () {
        this.batchUpdateOptions.forEach(option => {
          if (option.type === 'delete') {
            option.disabled = !this.hasDeletePermission
          }
        })
      }
    },
    computed: {
      data () {
        return this.sortData(this.$store.getters.getAllFreefields)
      },
      pageData () {
        return this.meetsFilterCriteria(this.data).slice((this.currentPage - 1) * 20, this.currentPage * 20)
      },
      meta () {
        return { count: this.meetsFilterCriteria(this.data).length, shown: this.pageData.length }
      },
      hasCreatePermission () {
        return this.$store.getters.checkIfUserHasRequiredPermissions(['system.view_freefield', 'system.add_freefield'])
      },
      hasDeletePermission () {
        return this.hasCreatePermission && this.$store.getters.checkIfUserHasRequiredPermissions(['system.delete_freefield'])
      }
    },
    watch: {
      pageData () {
        if (this.restoreTableSelection && typeof this.restoreTableSelection === 'function') {
          this.restoreTableSelection()
        }
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      let initPromises = []
      const fieldsAlreadyExist = !typeof store.getters.getResourceFieldsByEndpoint(payload.endpoint) === 'undefined'
      if (!fieldsAlreadyExist) {
        initPromises.push(store.dispatch('fetchOptions', payload))
      }
      try {
        await Promise.all(initPromises)
        // if (from.path !== '/') {
        let dataLoader = new DataLoader(store, [ payload ], true, 200)
        await dataLoader.loadData()
        // }
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    created () {
      this.endpoint = payload.endpoint
      this.moduleName = payload.moduleName
      this.payload = payload
      this.generateForm()
      this.updateBatchUpdateOptions()
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('freefields').offsetHeight
          const headerHeight = document.getElementById('freefields-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    }
  }
</script>

<style scoped>
  #freefields-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Freefields/Freefields.vue